"use client";

import { useMemo, useState } from "react";
import Link from "next/link";
import { safeSessionSet } from "@/lib/browser-storage";
import { buildSEOAgentHref, getSEOIntentConfigForSource } from "@/lib/seo-intents";

interface GemstoneDef {
  id: string;
  name: string;
  sanskrit: string;
  graha: string;
  symbol: string;
  rashi: string;
  substitute: string;
  metal: string;
  finger: string;
  dayTime: string;
  mantra: string;
  benefits: string[];
  caution: string;
}

const GEMSTONES: GemstoneDef[] = [
  {
    id: "ruby",
    name: "Ruby",
    sanskrit: "Manikya (माणिक्य)",
    graha: "Sun (Surya ☀️)",
    symbol: "💎",
    rashi: "Leo (Simha)",
    substitute: "Red Garnet / Star Ruby",
    metal: "Gold or Copper",
    finger: "Ring Finger (Anamika)",
    dayTime: "Sunday morning during Shukla Paksha (sunrise)",
    mantra: "Om Hram Hreem Hroum Sah Suryaya Namah (108 times)",
    benefits: [
      "Boosts executive authority & leadership",
      "Enhances vitality & heart health",
      "Favorable for government & administration",
    ],
    caution:
      "Avoid wearing if Sun is in 6th, 8th, or 12th house without expert chart verification.",
  },
  {
    id: "pearl",
    name: "Natural Pearl",
    sanskrit: "Moti (मोती)",
    graha: "Moon (Chandra 🌙)",
    symbol: "⚪",
    rashi: "Cancer (Kark)",
    substitute: "Moonstone",
    metal: "Pure Silver",
    finger: "Little Finger (Kanishtha)",
    dayTime: "Monday morning during Shukla Paksha",
    mantra: "Om Shram Shreem Shroum Sah Chandraya Namah (108 times)",
    benefits: [
      "Soothes anxiety, depression & mood swings",
      "Brings emotional clarity & peace of mind",
      "Improves relationship with mother",
    ],
    caution:
      "Avoid pairing with Blue Sapphire (Neelam) or Diamond (Heera) unless specifically prescribed.",
  },
  {
    id: "red_coral",
    name: "Red Coral",
    sanskrit: "Moonga (मूंगा)",
    graha: "Mars (Mangal 🔴)",
    symbol: "🪸",
    rashi: "Aries & Scorpio (Mesh / Vrishchik)",
    substitute: "Red Agate / Carnelian",
    metal: "Copper, Gold, or Silver",
    finger: "Ring Finger (Anamika)",
    dayTime: "Tuesday morning (1 hour after sunrise)",
    mantra: "Om Kram Kreem Kroum Sah Bhaumaya Namah (108 times)",
    benefits: [
      "Enhances physical courage, stamina & drive",
      "Supports real estate & property ventures",
      "Aids in blood circulation & vitality",
    ],
    caution: "Those with aggressive temperament should verify Mars placement before wearing.",
  },
  {
    id: "emerald",
    name: "Emerald",
    sanskrit: "Panna (पन्ना)",
    graha: "Mercury (Budh 🟢)",
    symbol: "💚",
    rashi: "Gemini & Virgo (Mithun / Kanya)",
    substitute: "Peridot / Green Tourmaline",
    metal: "Gold or Bronze (Kansa)",
    finger: "Little Finger (Kanishtha)",
    dayTime: "Wednesday morning during waxing moon",
    mantra: "Om Bram Breem Broum Sah Budhaya Namah (108 times)",
    benefits: [
      "Sharpens intellect, memory & speech",
      "Excellent for commerce, trading & coding",
      "Calms the nervous system",
    ],
    caution: "Avoid wearing cracked or flawed emeralds, as flawed stones harm communication.",
  },
  {
    id: "yellow_sapphire",
    name: "Yellow Sapphire",
    sanskrit: "Pukhraj (पुखराज)",
    graha: "Jupiter (Guru 🟡)",
    symbol: "💛",
    rashi: "Sagittarius & Pisces (Dhanu / Meen)",
    substitute: "Yellow Topaz / Citrine (Sunela)",
    metal: "Gold or Panchadhatu",
    finger: "Index Finger (Tarjani)",
    dayTime: "Thursday morning during Shukla Paksha",
    mantra: "Om Gram Greem Groum Sah Gurave Namah (108 times)",
    benefits: [
      "Brings wisdom, spiritual growth & higher learning",
      "Attracts financial prosperity & progeny blessings",
      "Enhances marital bliss for women",
    ],
    caution:
      "Highly auspicious for most natives, but verify 1st, 5th, or 9th house lordship first.",
  },
  {
    id: "diamond",
    name: "Diamond / White Sapphire",
    sanskrit: "Heera / Safed Pukhraj (हीरा)",
    graha: "Venus (Shukra ⚪)",
    symbol: "💎",
    rashi: "Taurus & Libra (Vrishabh / Tula)",
    substitute: "White Zircon / Opal",
    metal: "Platinum, White Gold, or Silver",
    finger: "Middle Finger (Madhyama) or Little Finger",
    dayTime: "Friday morning at sunrise",
    mantra: "Om Dram Dreem Droum Sah Shukraya Namah (108 times)",
    benefits: [
      "Enhances artistic creativity, glamour & charisma",
      "Attracts luxury, fine vehicles & wealth",
      "Strengthens romantic harmony",
    ],
    caution:
      "Avoid pairing with Ruby or Red Coral due to planetary enmity between Venus and Sun/Mars.",
  },
  {
    id: "blue_sapphire",
    name: "Blue Sapphire",
    sanskrit: "Neelam (नीलम)",
    graha: "Saturn (Shani 🪐)",
    symbol: "🔷",
    rashi: "Capricorn & Aquarius (Makar / Kumbh)",
    substitute: "Amethyst (Katela) / Iolite",
    metal: "Panchadhatu, Iron, or White Gold",
    finger: "Middle Finger (Madhyama)",
    dayTime: "Saturday evening (twilight)",
    mantra: "Om Pram Preem Proum Sah Shanaischaraya Namah (108 times)",
    benefits: [
      "Rapid career elevation & public recognition",
      "Removes chronic delays & hurdles",
      "Sharpens discipline and focus",
    ],
    caution:
      "Always test for 3 days under pillow before permanently wearing due to intense energetic potency.",
  },
  {
    id: "hessonite",
    name: "Hessonite Garnet",
    sanskrit: "Gomed (गोमेद)",
    graha: "Rahu (North Node 🐉)",
    symbol: "🟤",
    rashi: "Rahu-ruled / Shadow Graha",
    substitute: "Orange Zircon",
    metal: "Silver or Ashtadhatu",
    finger: "Middle Finger (Madhyama)",
    dayTime: "Saturday night or Wednesday night",
    mantra: "Om Bhram Bhreem Bhroum Sah Rahave Namah (108 times)",
    benefits: [
      "Success in foreign travel & tech innovation",
      "Overcomes hidden enemies & legal tangles",
      "Dispels mental confusion",
    ],
    caution:
      "Only wear during active Rahu Mahadasha/Antardasha after consulting an expert Jyotishi.",
  },
  {
    id: "cats_eye",
    name: "Cat's Eye Chrysoberyl",
    sanskrit: "Lehsuniya (लहसुनिया)",
    graha: "Ketu (South Node ☄️)",
    symbol: "👁️",
    rashi: "Ketu-ruled / Shadow Graha",
    substitute: "Tiger's Eye",
    metal: "Silver or Panchadhatu",
    finger: "Little Finger or Middle Finger",
    dayTime: "Tuesday or Thursday midnight / twilight",
    mantra: "Om Stram Streem Stroum Sah Ketave Namah (108 times)",
    benefits: [
      "Deep spiritual insight & occult wisdom",
      "Protects against sudden accidents & evil eye",
      "Detaches mind from toxic attachments",
    ],
    caution: "Do not wear without a specific spiritual or remedial objective.",
  },
];

export default function GemstoneRecommendationStudio() {
  const [selectedId, setSelectedId] = useState<string>("yellow_sapphire");

  const gem = useMemo<GemstoneDef>(() => {
    return GEMSTONES.find((g) => g.id === selectedId) || GEMSTONES[4];
  }, [selectedId]);

  const handleConsult = () => {
    const prompt = `Can I safely wear ${gem.name} (${gem.sanskrit}) for ${gem.graha} according to my Janam Kundali? Please analyze its compatibility with my Lagna and active Dasha.`;
    try {
      safeSessionSet("seo_entry_intent", "chart_report");
      safeSessionSet("seo_entry_path", "/gemstones");
      safeSessionSet("seo_entry_prompt", prompt);
      safeSessionSet("seo_entry_auto_send", "1");
    } catch {
      // Storage fallback
    }
  };

  return (
    <div className="rounded-2xl border border-border-default bg-surface-raised p-5 sm:p-8 shadow-sm my-8">
      <StudioHeader />
      <GemstoneSelector selectedId={selectedId} onSelect={setSelectedId} />
      <GemstoneDetailCard gem={gem} onConsult={handleConsult} />
    </div>
  );
}

function StudioHeader() {
  return (
    <div className="text-center max-w-2xl mx-auto mb-6">
      <span className="text-eyebrow font-bold uppercase tracking-wider text-intent-brand">
        Vedic Ratna Shastra
      </span>
      <h2 className="text-title font-bold text-text-primary mt-1 font-serif">
        Navaratna Gemstone Recommendation &amp; Wearing Protocol (रत्न चयन)
      </h2>
      <p className="text-body-small text-text-secondary mt-1">
        Select your ruling planet or Rashi to find the prescribed Vedic gemstone, metal, auspicious
        finger, energization mantra, and precautions.
      </p>
    </div>
  );
}

function GemstoneSelector({
  selectedId,
  onSelect,
}: {
  selectedId: string;
  onSelect: (id: string) => void;
}) {
  return (
    <div className="mb-6">
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2">
        {GEMSTONES.map((g) => {
          const isSelected = g.id === selectedId;
          return (
            <button
              key={g.id}
              type="button"
              onClick={() => onSelect(g.id)}
              className={`p-3 rounded-xl border text-left transition-all ${
                isSelected
                  ? "border-intent-brand bg-surface-brand-subtle shadow-sm ring-2 ring-intent-brand/20 text-text-primary"
                  : "border-border-default bg-surface-raised hover:bg-surface-warm text-text-secondary"
              }`}
            >
              <span className="text-lg block mb-1">{g.symbol}</span>
              <span className="block text-body-small font-bold text-text-primary">{g.name}</span>
              <span className="block text-caption text-text-muted truncate">
                {g.sanskrit.split(" ")[0]}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function GemstoneDetailCard({ gem, onConsult }: { gem: GemstoneDef; onConsult: () => void }) {
  const chatHref = buildSEOAgentHref(getSEOIntentConfigForSource("/gemstones"), "/gemstones", "en");

  return (
    <div className="rounded-xl border border-border-default bg-surface-raised p-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pb-5 border-b border-border-default">
        <div>
          <div className="flex items-center gap-3">
            <h3 className="text-xl font-bold font-serif text-text-primary">
              {gem.symbol} {gem.name} ({gem.sanskrit})
            </h3>
            <span className="px-3 py-0.5 rounded-full text-caption font-bold bg-intent-brand/10 text-intent-brand">
              {gem.graha}
            </span>
          </div>
          <p className="text-caption text-text-secondary mt-1">
            Governs Rashi: <strong className="text-text-primary">{gem.rashi}</strong> &middot;
            Upratna: <span className="text-text-primary font-medium">{gem.substitute}</span>
          </p>
        </div>

        <a
          href={chatHref}
          onClick={onConsult}
          className="shrink-0 inline-flex items-center justify-center rounded-lg bg-surface-brand-strong px-5 py-2.5 text-button font-bold text-text-on-brand hover:bg-surface-brand-strong-hover transition-colors shadow-sm"
        >
          Verify My Chart for {gem.name} ✦
        </a>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-5">
        <ProtocolItem label="Prescribed Metal" value={gem.metal} />
        <ProtocolItem label="Auspicious Finger" value={gem.finger} />
        <ProtocolItem label="Wearing Day & Time" value={gem.dayTime} />
        <ProtocolItem label="Primary Substitute" value={gem.substitute} />
      </div>

      <div className="mt-5 p-4 rounded-xl bg-surface-raised border border-border-default">
        <p className="text-caption font-bold uppercase tracking-wider text-intent-brand mb-1">
          Prana Pratishtha Energization Mantra (108 Chants)
        </p>
        <p className="text-body-small font-serif italic text-text-primary">
          &quot;{gem.mantra}&quot;
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
        <div className="p-4 rounded-xl bg-success/5 border border-success/20">
          <p className="text-caption font-bold uppercase tracking-wider text-success mb-1.5">
            Key Vedic Benefits
          </p>
          <ul className="space-y-1 text-caption text-text-primary">
            {gem.benefits.map((b, i) => (
              <li key={i} className="flex items-center gap-1.5">
                <span className="text-success">✓</span>
                <span>{b}</span>
              </li>
            ))}
          </ul>
        </div>

        <div className="p-4 rounded-xl bg-warning/5 border border-warning/20">
          <p className="text-caption font-bold uppercase tracking-wider text-warning mb-1.5">
            Shastric Precaution
          </p>
          <p className="text-caption text-text-primary leading-relaxed">{gem.caution}</p>
        </div>
      </div>

      <div className="mt-5 pt-4 border-t border-border-default flex flex-col sm:flex-row items-center justify-between gap-3 text-caption">
        <span className="text-text-secondary">
          Need a personalized gemstone prescription based on exact Lagna and Mahadasha?
        </span>
        <Link
          href="/free-kundali/"
          className="font-semibold text-intent-brand hover:underline flex items-center gap-1"
        >
          Check Janam Kundali First →
        </Link>
      </div>
    </div>
  );
}

function ProtocolItem({ label, value }: { label: string; value: string }) {
  return (
    <div className="p-3 rounded-lg bg-surface-raised border border-border-default">
      <span className="block text-eyebrow font-bold uppercase tracking-wider text-text-muted">
        {label}
      </span>
      <span className="block text-caption font-bold text-text-primary mt-0.5">{value}</span>
    </div>
  );
}
